import { Controller } from "@hotwired/stimulus";

// intlTelInput is loaded from CDN in the layout

export default class extends Controller {
  static targets = [ "code1", "code2", "code3", "code4", "code5", "code6",
    "checkCode", "validationFailed", "validationExpired", "validationSuccess",
    "form", "btnSubmit", "primaryUseContainer",
    "nameInput", "slugPreview", "slugValue", "slugStatus",
    "resendLink", "resendFeedback", "phoneInput" ]

  static values = {
    checkUrl: String,
    slugAvailable: { type: String, default: '✓ Beschikbaar' },
    slugTaken: { type: String, default: '✗ Niet beschikbaar' },
    slugReserved: { type: String, default: '✗ Gereserveerde naam' },
    slugError: { type: String, default: 'Kon niet controleren' },
    slugTooShort: { type: String, default: '✗ Minimaal 3 karakters' },
    recaptchaSiteKey: { type: String, default: '' }
  }

  connect() {
    this.updatePrimaryUseVisibility();
    this.slugCheckTimeout = null;
    this.currentSlug = null;
    this.slugAvailable = false;
    this.reservedNames = ['admin', 'www', 'api', 'app', 'mail', 'ftp', 'staging', 'test', 'demo', 'support', 'help', 'billing', 'login', 'signup', 'register'];

    // Check slug availability on page load if there's already a value (e.g., after validation error)
    if (this.hasNameInputTarget && this.nameInputTarget.value.trim().length >= 3) {
      this.generateSlug();
    }

    // Initialize intl-tel-input for phone field
    this.initPhoneInput();
  }

  initPhoneInput() {
    if (!this.hasPhoneInputTarget) return;
    if (typeof window.intlTelInput === 'undefined') return;

    this.iti = window.intlTelInput(this.phoneInputTarget, {
      countryOrder: ['nl', 'be', 'de', 'fr', 'gb', 'us'],
      initialCountry: 'auto',
      separateDialCode: true,
      loadUtilsOnInit: 'https://cdn.jsdelivr.net/npm/intl-tel-input@24.8.2/build/js/utils.js',
      geoIpLookup: (success, failure) => {
        fetch('https://ipinfo.io/json', { headers: { 'Accept': 'application/json' }})
          .then(response => response.json())
          .then(data => success(data.country ? data.country.toLowerCase() : 'nl'))
          .catch(() => failure());
      }
    });
  }

  generateSlug() {
    if (!this.hasNameInputTarget || !this.hasSlugValueTarget) return;

    const name = this.nameInputTarget.value;
    const slug = this.nameToSlug(name);

    if (slug.length >= 3) {
      this.slugPreviewTarget.classList.remove('d-none');
      this.slugValueTarget.textContent = slug;
      this.currentSlug = slug;
      this.nameInputTarget.setCustomValidity('');

      // Debounced availability check
      if (this.slugCheckTimeout) {
        clearTimeout(this.slugCheckTimeout);
      }
      this.showSlugStatus('checking');
      this.slugCheckTimeout = setTimeout(() => this.checkAvailability(slug), 500);
    } else {
      this.slugPreviewTarget.classList.add('d-none');
      this.currentSlug = null;
      this.slugAvailable = false;
      // Mark as invalid if form validation was already triggered
      if (this.formTarget.classList.contains('was-validated')) {
        this.nameInputTarget.setCustomValidity('Invalid');
      }
      if (this.hasSlugStatusTarget) {
        this.slugStatusTarget.innerHTML = '';
      }
    }
  }

  validateName() {
    if (!this.hasNameInputTarget) return;

    const slug = this.nameToSlug(this.nameInputTarget.value);
    if (slug.length < 3) {
      this.nameInputTarget.setCustomValidity('Invalid');
      this.slugPreviewTarget.classList.remove('d-none');
      this.slugValueTarget.textContent = slug || '-';
      this.showSlugStatus('too_short');
    } else if (!this.slugAvailable) {
      this.nameInputTarget.setCustomValidity('Invalid');
    } else {
      this.nameInputTarget.setCustomValidity('');
    }
  }

  validatePhone() {
    if (!this.hasPhoneInputTarget) return;

    const phoneValue = this.phoneInputTarget.value.trim();
    const digitsOnly = phoneValue.replace(/\D/g, '');

    if (digitsOnly.length < 6 || (this.iti && !this.iti.isValidNumber())) {
      this.phoneInputTarget.setCustomValidity('Invalid phone number');
    } else {
      this.phoneInputTarget.setCustomValidity('');
    }
  }

  nameToSlug(name) {
    return name
      .toLowerCase()
      .trim()
      .replace(/[,\s\.]+/g, '-')  // spaces, commas, dots to hyphens
      .replace(/[^-0-9a-z]/g, '') // remove invalid chars
      .replace(/-+/g, '-')        // no consecutive hyphens
      .replace(/^-|-$/g, '');     // no leading/trailing hyphens
  }

  checkAvailability(slug) {
    // Check reserved names first
    if (this.reservedNames.includes(slug)) {
      this.showSlugStatus('reserved');
      return;
    }

    // Check via API
    const url = `/signup/check-subdomain?subdomain=${encodeURIComponent(slug)}`;
    fetch(url, {
      method: 'GET',
      headers: { 'Accept': 'application/json' }
    })
    .then(response => response.json())
    .then(data => {
      if (slug === this.currentSlug) {
        this.showSlugStatus(data.available ? 'available' : 'taken');
      }
    })
    .catch(() => {
      if (slug === this.currentSlug) {
        this.showSlugStatus('error');
      }
    });
  }

  showSlugStatus(status) {
    if (!this.hasSlugStatusTarget) return;

    const statusEl = this.slugStatusTarget;
    statusEl.className = '';
    this.slugAvailable = (status === 'available');

    // Update input validity using HTML5 custom validity
    if (this.hasNameInputTarget) {
      if (status === 'available') {
        this.nameInputTarget.setCustomValidity('');
      } else if (status !== 'checking') {
        this.nameInputTarget.setCustomValidity('Invalid');
      }
    }

    switch(status) {
      case 'checking':
        statusEl.innerHTML = '<span class="spinner-border spinner-border-sm text-muted"></span>';
        break;
      case 'available':
        statusEl.innerHTML = `<span class="text-success">${this.slugAvailableValue}</span>`;
        break;
      case 'taken':
        statusEl.innerHTML = `<span class="text-danger">${this.slugTakenValue}</span>`;
        break;
      case 'reserved':
        statusEl.innerHTML = `<span class="text-danger">${this.slugReservedValue}</span>`;
        break;
      case 'error':
        statusEl.innerHTML = `<span class="text-warning">${this.slugErrorValue}</span>`;
        break;
      case 'too_short':
        statusEl.innerHTML = `<span class="text-danger">${this.slugTooShortValue}</span>`;
        break;
    }
  }

  togglePrimaryUse() {
    this.updatePrimaryUseVisibility();
  }

  updatePrimaryUseVisibility() {
    if (!this.hasPrimaryUseContainerTarget) return;

    const ticketshopCheckbox = document.getElementById('product_ticketshop');
    if (ticketshopCheckbox && ticketshopCheckbox.checked) {
      this.primaryUseContainerTarget.style.display = 'block';
    } else {
      this.primaryUseContainerTarget.style.display = 'none';
    }
  }

  submitForm(event) {
    this.formTarget.classList.remove('needs-validation');
    this.formTarget.classList.add('was-validated');

    let isValid = this.validateForm(this.formTarget);

    if (!isValid) {
      event.preventDefault();
      return;
    }

    event.preventDefault();

    // Set the full phone number with country code before submitting
    if (this.iti && this.hasPhoneInputTarget) {
      this.phoneInputTarget.value = this.iti.getNumber();
    }
    this.dispatch("disable-button", {target: this.btnSubmitTarget});

    // Execute reCAPTCHA v3 and submit
    if (this.recaptchaSiteKeyValue && typeof grecaptcha !== 'undefined') {
      grecaptcha.ready(() => {
        grecaptcha.execute(this.recaptchaSiteKeyValue, { action: 'signup' })
          .then((token) => {
            this.injectRecaptchaToken(token);
            this.formTarget.submit();
          })
          .catch(() => {
            this.formTarget.submit();
          });
      });
    } else {
      this.formTarget.submit();
    }
  }

  injectRecaptchaToken(token) {
    let input = this.formTarget.querySelector('input[name="recaptcha_token"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'recaptcha_token';
      this.formTarget.append(input);
    }
    input.value = token;
  }

  validateForm() {
    let isValid = true;
    let firstInvalidField = null;

    // Validate phone number
    if (this.hasPhoneInputTarget) {
      const phoneValue = this.phoneInputTarget.value.trim();
      const digitsOnly = phoneValue.replace(/\D/g, '');

      if (digitsOnly.length < 6 || (this.iti && !this.iti.isValidNumber())) {
        this.phoneInputTarget.setCustomValidity('Invalid phone number');
        if (!firstInvalidField) firstInvalidField = this.phoneInputTarget;
        isValid = false;
      } else {
        this.phoneInputTarget.setCustomValidity('');
      }
    }

    // Validate name/subdomain
    if (this.hasNameInputTarget) {
      if (!this.slugAvailable) {
        this.nameInputTarget.setCustomValidity('Invalid');
        const slug = this.nameToSlug(this.nameInputTarget.value);
        if (slug.length < 3) {
          this.slugPreviewTarget.classList.remove('d-none');
          this.slugValueTarget.textContent = slug || '-';
          this.showSlugStatus('too_short');
        } else {
          this.showSlugStatus('taken');
        }
        if (!firstInvalidField) firstInvalidField = this.nameInputTarget;
        isValid = false;
      } else {
        this.nameInputTarget.setCustomValidity('');
      }
    }

    // Check required fields
    let requiredFields = this.formTarget.querySelectorAll('textarea:required, input:required, select:required');
    for (const field of requiredFields) {
      if (!field.disabled && !field.validity.valid) {
        if (!firstInvalidField) firstInvalidField = field;
        isValid = false;
      }
    }

    if (firstInvalidField) {
      firstInvalidField.focus();
    }

    return isValid;
  }

  pasteFromClipboard(e) {
    e.preventDefault();

    let paste = (e.clipboardData || window.clipboardData).getData("text");
    paste = paste.toUpperCase().replace('-', '').trim();
    if (paste.length == 6) {
      this.code1Target.value = paste.charAt(0);
      this.code2Target.value = paste.charAt(1);
      this.code3Target.value = paste.charAt(2);
      this.code4Target.value = paste.charAt(3);
      this.code5Target.value = paste.charAt(4);
      this.code6Target.value = paste.charAt(5);
      this.check();
    }
  }

  next(e) {
    if (e.key.length == 1) {
      var next = e.srcElement;
      while (next = next.nextElementSibling) {
        if (next == null)
            break;
        if (next.tagName.toLowerCase() == "input") {
            next.focus();
            break;
        }
      }
    }
  }

  check() {
    let code = this.code1Target.value +
      this.code2Target.value +
      this.code3Target.value +
      this.code4Target.value +
      this.code5Target.value +
      this.code6Target.value;

    if (code.length == 6) {
      this.checkCodeTarget.classList.remove('d-none');
      this.validationExpiredTarget.classList.add('d-none');
      this.validationFailedTarget.classList.add('d-none');

      // validate code on server
      fetch(this.formTarget.action, {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector("meta[name='csrf-token']").content
        },
        body: JSON.stringify({ "code": code })
      })
       .then(response => response.json())
       .then(response => this.parseResponse(response));
    }
  }

  parseResponse(response) {
    this.checkCodeTarget.classList.add('d-none');

    // Handle session expired - reload page to get new CSRF token
    if (response.error === 'session_expired') {
      window.location.reload();
      return;
    }

    this.code1Target.value = '';
    this.code2Target.value = '';
    this.code3Target.value = '';
    this.code4Target.value = '';
    this.code5Target.value = '';
    this.code6Target.value = '';
    this.code1Target.focus();

    if (response.result == "OK") {
      // show
      this.validationSuccessTarget.classList.remove('d-none');
      // redirect - continue (extract locale from current URL path)
      const pathParts = window.location.pathname.split('/');
      const locale = pathParts[1] || 'nl';
      window.location = '/' + locale + '/' + response.signup_key + '/wrap-up';
    } else if (response.result == "NOK") {
      this.validationFailedTarget.classList.remove('d-none');
    } else if (response.result == "EXPIRED") {
      this.validationExpiredTarget.classList.remove('d-none');
    }
  }

  resendCode(event) {
    event.preventDefault();

    // Get the resend URL from the current form action, replacing validate-code with resend-code
    const resendUrl = this.formTarget.action.replace('validate-code', 'resend-code');

    // Disable the link temporarily
    if (this.hasResendLinkTarget) {
      this.resendLinkTarget.style.pointerEvents = 'none';
      this.resendLinkTarget.style.opacity = '0.5';
    }

    fetch(resendUrl, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector("meta[name='csrf-token']").content
      }
    })
    .then(response => response.json())
    .then(response => {
      // Handle session expired - reload page to get new CSRF token
      if (response.error === 'session_expired') {
        window.location.reload();
        return;
      }

      if (response.result === "OK") {
        // Show success feedback
        if (this.hasResendFeedbackTarget) {
          this.resendFeedbackTarget.classList.remove('d-none');
          // Hide after 3 seconds
          setTimeout(() => {
            this.resendFeedbackTarget.classList.add('d-none');
          }, 3000);
        }
      }
      // Re-enable the link after 30 seconds (rate limiting)
      setTimeout(() => {
        if (this.hasResendLinkTarget) {
          this.resendLinkTarget.style.pointerEvents = 'auto';
          this.resendLinkTarget.style.opacity = '1';
        }
      }, 30000);
    })
    .catch(() => {
      // Re-enable on error
      if (this.hasResendLinkTarget) {
        this.resendLinkTarget.style.pointerEvents = 'auto';
        this.resendLinkTarget.style.opacity = '1';
      }
    });
  }
}
